// JOBE-0044.4: chip vocabulary — friendly tokens (what the user types
// and what the chip caption shows) mapped to the wire predicate
// (`GET /api/entities` parameter name) and the URL param name.
//
// JOBE-0044.10 fix: this file used to ship only the Phase 2 subset of
// predicates the text-input parser surfaced. The composer's predicate
// picker emits the full umbrella §R4 vocabulary, so the translator
// tables had to widen to round-trip those chips through the URL.
//
// BFF wiring posture: only the original Phase 2 subset has a
// corresponding `/api/entities` query parameter today (entities_list.go
// allowlist). Picker-only predicates (`watcher`, `dept`, `parent`,
// `from`, `by-run`, `procedure`, `cites`, `about`, `blocked-by`,
// `created`, `field.X`) carry `bffWired: false`. We URL-persist them
// (so the chip survives reload + bookmarking) but suppress them from
// the fetch query — sending them today would 400 `bad_request`. The
// chip row visually surfaces them as filters; when the BFF widens its
// allowlist (per ADR-0023 §D3 + JOBE-0044.15) the toggle here flips
// to `bffWired: true` and they start participating in the fetch.

// FRIENDLY_TOKENS: friendly-token → { wirePredicate, urlParam,
// bffWired, kind }.
// `kind` is the value semantics: 'set' (default — multi-value OR,
// merges on append) vs 'replace' (single value, last-write-wins;
// dates are the canonical case so the user can re-pick a bound).
export const FRIENDLY_TOKENS = {
  // ── Phase 2 subset — BFF-accepted today ──────────────────────
  type:     { wirePredicate: 'type',         urlParam: 'type',         bffWired: true },
  owner:    { wirePredicate: 'assignedTo',   urlParam: 'owner',        bffWired: true },
  by:       { wirePredicate: 'committedBy',  urlParam: 'by',           bffWired: true },
  project:  { wirePredicate: 'inProject',    urlParam: 'project',      bffWired: true },
  in:       { wirePredicate: 'inCollection', urlParam: 'in',           bffWired: true },
  since:    { wirePredicate: 'committedAt>=', urlParam: 'since',       bffWired: true, kind: 'replace' },
  until:    { wirePredicate: 'committedAt<',  urlParam: 'until',       bffWired: true, kind: 'replace' },
  q:        { wirePredicate: 'q',            urlParam: 'q',            bffWired: true },

  // ── Picker-only — URL-persist, suppress from fetch (today) ───
  // Wire predicate names mirror ADR-0023 §D3 / the omni-parser
  // vocabulary so a future BFF allowlist widening is a one-flag flip.
  watcher:     { wirePredicate: 'watches',         urlParam: 'watcher',    bffWired: false },
  dept:        { wirePredicate: 'roleInDepartment', urlParam: 'dept',      bffWired: false },
  parent:      { wirePredicate: 'partOf',          urlParam: 'parent',     bffWired: false },
  from:        { wirePredicate: 'derivedFrom',     urlParam: 'from',       bffWired: false },
  'by-run':    { wirePredicate: 'producedByRun',   urlParam: 'by-run',     bffWired: false },
  procedure:   { wirePredicate: 'procedureUrn',    urlParam: 'procedure',  bffWired: false },
  cites:       { wirePredicate: 'references',      urlParam: 'cites',      bffWired: false },
  about:       { wirePredicate: 'relatesTo',       urlParam: 'about',      bffWired: false },
  'blocked-by': { wirePredicate: 'dependsOn',      urlParam: 'blocked-by', bffWired: false },
  created:     { wirePredicate: 'createdAt>=',     urlParam: 'created',    bffWired: false, kind: 'replace' },
};

// Friendly-token aliases per umbrella §R4. The parser maps any of these
// to the canonical friendly token before emitting a chip.
//
// NOTE: 'created<' intentionally has no alias here — the picker only
// surfaces a single `created>=` predicate today, so aliasing
// 'created<' → 'until' would silently coerce a created-before filter
// to a committed-before filter. PR #697 removed the old buggy alias.
export const FRIENDLY_ALIASES = {
  creator: 'by',
  'updated>=': 'since',
  'updated<': 'until',
  'created>=': 'created',
};

// DEFERRED_TOKENS: text-input-parser-only gate per the .4 design —
// these tokens surface the "upgrade to filter picker" hint in
// `ChipTextInput`, but they still have FRIENDLY_TOKENS entries so the
// picker's chip output (which bypasses parseChipInput) round-trips
// through the URL writer and the BFF query builder. Keeping the
// parser narrow preserves the .4 text-input UX promise (only the
// pinned subset accepts inline); the picker is the canonical entry
// point for the rest.
export const DEFERRED_TOKENS = new Set([
  'watcher', 'dept', 'parent', 'from', 'by-run', 'procedure',
  'cites', 'about', 'blocked-by', 'created',
]);

// CHIPS_BY_URL_PARAM: reverse index for `chipsFromSearchParams` — URL
// param name → friendly token. Built once at module load.
const CHIPS_BY_URL_PARAM = (() => {
  const m = new Map();
  for (const [friendly, entry] of Object.entries(FRIENDLY_TOKENS)) {
    m.set(entry.urlParam, friendly);
  }
  return m;
})();

// isReplaceChip: chips whose append-into-existing semantics is
// "replace" rather than "merge". Date bounds are the canonical case
// — merging `last-7d` with `2026-04-01` produces an incoherent
// `since=last-7d,2026-04-01`.
export function isReplaceChip(friendlyToken) {
  if (!friendlyToken) return false;
  if (friendlyToken.startsWith('field.')) return false;
  const entry = FRIENDLY_TOKENS[friendlyToken];
  return entry?.kind === 'replace';
}

// parseChipInput: take the raw chip text-input value (e.g., "by:iris",
// "type:briefing,report", "vendor obs"), return one of:
//   { kind: 'predicate', friendlyToken, wirePredicate, values: string[] }
//   { kind: 'predicate', friendlyToken: 'q', wirePredicate: 'q', values: [text] }   // bare text → q:
//   { kind: 'unrecognized', token, reason: 'deferred' | 'unknown' }
//   null  // empty input
export function parseChipInput(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  const colonIdx = trimmed.indexOf(':');
  if (colonIdx < 0) {
    // Bare text — treat as a `q:` chip.
    return {
      kind: 'predicate',
      friendlyToken: 'q',
      wirePredicate: FRIENDLY_TOKENS.q.wirePredicate,
      values: [trimmed],
    };
  }

  const rawToken = trimmed.slice(0, colonIdx).trim().toLowerCase();
  const rawValue = trimmed.slice(colonIdx + 1).trim();
  if (!rawValue) return null;

  // Resolve alias if any.
  const token = FRIENDLY_ALIASES[rawToken] || rawToken;

  // field.X: schema-relative attribute filter — synthesised at chip
  // shape time, not in the static FRIENDLY_TOKENS table. The picker is
  // the canonical entry surface; the text-input parser still defers.
  if (rawToken.startsWith('field.')) {
    return { kind: 'unrecognized', token: rawToken, reason: 'deferred' };
  }

  if (DEFERRED_TOKENS.has(rawToken)) {
    return { kind: 'unrecognized', token: rawToken, reason: 'deferred' };
  }

  const entry = FRIENDLY_TOKENS[token];
  if (!entry) {
    return { kind: 'unrecognized', token: rawToken, reason: 'unknown' };
  }

  // q: keeps the value as a single string (spaces allowed).
  // Other predicates accept comma-separated value lists.
  const values = token === 'q'
    ? [rawValue]
    : rawValue.split(',').map((s) => s.trim()).filter(Boolean);

  if (values.length === 0) return null;

  return {
    kind: 'predicate',
    friendlyToken: token,
    wirePredicate: entry.wirePredicate,
    values,
  };
}

// chipsToSearchParams: given the active chip array (including the
// mandatory scope chip at index 0), return the URLSearchParams shape
// the URL writer should commit. Scope chip is ignored — it's the
// always-on baseline, not a URL param.
//
// Picker-only (`bffWired: false`) chips ARE persisted to the URL so
// the chip survives reload + bookmarking; they're stripped from the
// fetch query separately (see chipsToFetchQuery).
//
// `field.X` chips encode as `field.<name>=…` URL params. Multiple
// field chips coexist (each is its own URL param), so there is no
// merge / collision.
//
// JOBE-0101: optional `namespace` prefixes every emitted URL key with
// `<namespace>.` so multiple <EntityBrowser> instances can share one
// `?…` querystring without colliding. When `namespace` is unset the
// keys remain unprefixed (preserving the library's existing URL shape).
export function chipsToSearchParams(chips, { namespace } = {}) {
  const params = new URLSearchParams();
  const prefix = namespace ? `${namespace}.` : '';
  for (const chip of chips) {
    if (chip.kind !== 'predicate') continue;
    if (!chip.values || chip.values.length === 0) continue;
    if (chip.friendlyToken && chip.friendlyToken.startsWith('field.')) {
      params.set(`${prefix}${chip.friendlyToken}`, chip.values.join(','));
      continue;
    }
    const entry = FRIENDLY_TOKENS[chip.friendlyToken];
    if (!entry) continue;
    params.set(`${prefix}${entry.urlParam}`, chip.values.join(','));
  }
  return params;
}

// resolveRelativeDate: client-side resolver for the parser-recognised
// RELATIVE_DATE_TOKENS plus YYYY-MM-DD bare dates. Returns an RFC3339
// timestamp the BFF's `time.RFC3339` parser accepts, or null if the
// input is not a known shape (the caller drops the chip from the
// fetch in that case so the BFF never sees a 400).
//
// `bound` is 'since' (lower bound — start of period) or 'until'
// (upper bound — start of the day AFTER the period, so the BFF's
// `committedAt < until` matches the natural calendar meaning of
// "before X").
//
// `now` is injected for tests; defaults to `new Date()`.
export function resolveRelativeDate(token, bound, now = new Date()) {
  if (!token) return null;
  const t = String(token).trim();

  // YYYY-MM-DD bare date → midnight UTC of that day (since), midnight
  // UTC of the next day (until — exclusive bound).
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const start = new Date(Date.UTC(y, mo, d));
    if (bound === 'until') start.setUTCDate(start.getUTCDate() + 1);
    return start.toISOString();
  }

  // Already an RFC3339-shaped string — let it through unchanged.
  // Rough shape check: must contain a 'T' between date + time.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(t)) return t;

  // Relative-date tokens. Anchor to the start of the local day in
  // UTC so the bound is a stable RFC3339 stamp.
  const startOfDay = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
  ));
  const day = 24 * 60 * 60 * 1000;

  const offsetDays = (n) => {
    const d = new Date(startOfDay.getTime() + n * day);
    return d.toISOString();
  };
  // `until` is exclusive: "today" as an upper bound means the start
  // of tomorrow so the day's own rows are included.
  const upperOf = (n) => offsetDays(n + 1);

  switch (t) {
    case 'today':       return bound === 'until' ? upperOf(0)   : offsetDays(0);
    case 'yesterday':   return bound === 'until' ? upperOf(-1)  : offsetDays(-1);
    case 'last-7d':     return bound === 'until' ? upperOf(0)   : offsetDays(-7);
    case 'last-30d':    return bound === 'until' ? upperOf(0)   : offsetDays(-30);
    case 'last-90d':    return bound === 'until' ? upperOf(0)   : offsetDays(-90);
    case 'last-365d':   return bound === 'until' ? upperOf(0)   : offsetDays(-365);
    case 'this-week': {
      // ISO week starts Monday. dow: Sun=0..Sat=6 → days since Monday.
      const dow = startOfDay.getUTCDay();
      const sinceMon = (dow + 6) % 7;
      return bound === 'until' ? upperOf(0) : offsetDays(-sinceMon);
    }
    case 'last-week': {
      const dow = startOfDay.getUTCDay();
      const sinceMon = (dow + 6) % 7;
      // last week = [prev Monday, this Monday)
      return bound === 'until' ? offsetDays(-sinceMon) : offsetDays(-sinceMon - 7);
    }
    case 'this-month': {
      const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      return bound === 'until' ? upperOf(0) : first.toISOString();
    }
    default:
      return null;
  }
}

// chipsToFetchQuery: given the active chip array, return the query
// string the BFF expects (`GET /api/entities?…`). Translates friendly
// → wire (since:, until: → committedAt>= / committedAt<).
//
// Picker-only (`bffWired: false`) chips and `field.X` chips are
// dropped here — the BFF doesn't accept them today. They still
// participate in the URL state (via chipsToSearchParams) so the chip
// survives reload, but they don't participate in the live query.
export function chipsToFetchQuery(chips, extra = {}, opts = {}) {
  const params = new URLSearchParams();
  const now = opts.now || new Date();
  for (const chip of chips) {
    if (chip.kind !== 'predicate') continue;
    if (!chip.values || chip.values.length === 0) continue;
    // Drop field.X — no BFF support today.
    if (chip.friendlyToken && chip.friendlyToken.startsWith('field.')) continue;
    const entry = FRIENDLY_TOKENS[chip.friendlyToken];
    // Drop picker-only chips without BFF support.
    if (!entry || entry.bffWired !== true) continue;

    const wire = chip.wirePredicate;
    const value = chip.values.join(',');
    // Map our internal wire labels onto the openapi-pinned query param
    // names. `committedAt>=` → `since`, `committedAt<` → `until`; the
    // rest are the openapi names verbatim. Date values are resolved
    // client-side (relative-date tokens, YYYY-MM-DD) into RFC3339
    // before the BFF's `time.RFC3339` parser sees them — otherwise
    // `since=today` 400s.
    if (wire === 'committedAt>=') {
      const v = resolveRelativeDate(chip.values[0], 'since', now);
      if (v) params.set('since', v);
      continue;
    }
    if (wire === 'committedAt<') {
      const v = resolveRelativeDate(chip.values[0], 'until', now);
      if (v) params.set('until', v);
      continue;
    }
    params.set(wire, value);
  }
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== null && v !== '') {
      params.set(k, String(v));
    }
  }
  return params;
}

// chipsFromSearchParams: hydrate the chip array from a URLSearchParams.
// Always prepends the mandatory scope chip.
//
// Walks the input params directly (rather than the FRIENDLY_TOKENS
// table) so `field.X` URL params — which are dynamic, not in the
// static table — round-trip too. Within the predicates, the chip
// order follows the URL's iteration order; the scope chip is always
// first.
//
// JOBE-0101: optional `namespace` reads only keys with the
// `<namespace>.` prefix. The hook splits on the FIRST `.` only so
// `tasks.field.priority=low` resolves to namespace `tasks` and chip
// key `field.priority`. When `namespace` is unset, only unprefixed
// keys are read.
export function chipsFromSearchParams(params, { namespace } = {}) {
  const chips = [makeScopeChip()];
  const prefix = namespace ? `${namespace}.` : '';
  const stripped = new URLSearchParams();
  if (namespace) {
    for (const [key, raw] of params.entries()) {
      if (!key.startsWith(prefix)) continue;
      // Split on the FIRST `.` only — `tasks.field.priority` → `field.priority`.
      stripped.set(key.slice(prefix.length), raw);
    }
  } else {
    // Unset namespace: pass through only keys with NO dot prefix that
    // would otherwise be claimed by a sibling namespaced instance.
    // For library back-compat we accept every key as-is (matches today).
    for (const [key, raw] of params.entries()) {
      stripped.set(key, raw);
    }
  }
  // Stable order: walk the static table first (preserves the historical
  // chip order), then sweep any dynamic field.X params.
  for (const [friendlyToken, entry] of Object.entries(FRIENDLY_TOKENS)) {
    const raw = stripped.get(entry.urlParam);
    if (raw == null || raw === '') continue;
    const values = friendlyToken === 'q'
      ? [raw]
      : raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (values.length === 0) continue;
    chips.push({
      kind: 'predicate',
      friendlyToken,
      wirePredicate: entry.wirePredicate,
      values,
    });
  }
  for (const [key, raw] of stripped.entries()) {
    if (!key.startsWith('field.')) continue;
    if (raw == null || raw === '') continue;
    const values = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (values.length === 0) continue;
    chips.push({
      kind: 'predicate',
      friendlyToken: key,
      wirePredicate: key.slice('field.'.length),
      values,
    });
  }
  return chips;
}

// makeScopeChip: the always-on, non-removable baseline.
export function makeScopeChip() {
  return {
    kind: 'scope',
    label: 'Everything I can read',
  };
}

// hashFilters: a stable, cheap signature of the active filter set, used
// as the in-memory `(filterHash, cursor)` page-cache key per R11.
export function hashFilters(chips, sort) {
  const params = chipsToFetchQuery(chips);
  // Sort params for deterministic hash regardless of insertion order.
  const parts = [...params.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`);
  parts.push(`sort=${sort || 'lastChangedAt:desc'}`);
  return parts.join('&');
}

// CHIPS_BY_URL_PARAM is exposed indirectly for callers that need the
// reverse lookup (none today, but the build catches accidental dead
// code).
export { CHIPS_BY_URL_PARAM };
